import { useIsChromeExtension } from "@src/features/chrome";
import { Logo } from "@src/shared/components/general/Logo";
import { cn } from "@src/shared/lib/utils";

export interface UnauthenticatedLayoutV2Props {
  children: React.ReactNode;
}

/**
 * Shell for the pre-auth screens (login, sign-up, 2FA).
 *
 * In the chrome extension the surrounding panel already shows the Slate logo in
 * its dialog header, so the layout drops its own header and viewport-height
 * centering — the panel is a fixed-height overlay, and `dvh` units there
 * resolve against the host page viewport, which pushes content out of view.
 */
export function UnauthenticatedLayoutV2({
  children,
}: UnauthenticatedLayoutV2Props) {
  const isChromeExtension = useIsChromeExtension();

  return (
    <div
      className={cn(
        "flex flex-col bg-background text-foreground pl-[var(--safe-area-left)] pr-[var(--safe-area-right)]",
        isChromeExtension ? "h-full" : "min-h-dvh",
      )}
    >
      {isChromeExtension ? null : (
        <header className="flex shrink-0 flex-col border-b border-border pt-[var(--safe-area-top)]">
          <div className="flex h-11 items-center px-5">
            <Logo className="h-4" />
          </div>
        </header>
      )}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          isChromeExtension
            ? "overflow-y-auto px-4"
            : "px-6 py-10 pb-[max(2.5rem,var(--safe-area-bottom))]",
        )}
      >
        {/* Vertical auto margins centre the content while it fits and collapse
            to zero once it does not, so a screen taller than the panel scrolls
            from its top edge. Vertical `items-center` / `justify-center` would
            push the overhang into negative scroll space the user cannot reach.
            Horizontal `items-center` is safe: pages size themselves (often with
            a max-width) and must stay centred on the web shell too. */}
        <div className="my-auto flex w-full flex-col items-center">
          {children}
        </div>
      </div>
    </div>
  );
}
